import type {ReactElement} from 'react';
import React from 'react';
import {ContextualMenuFontButton} from '@Components/contextual-menu/components/contextual-menu-font-button';
import {ItemStylingOptions} from '@Components/poster-editor/components/poster-editing-side-panel/components/item-styling-options';
import {BaseStylingOptions} from '@Components/poster-editor/components/poster-editing-side-panel/components/base-styling-options';
import {TOOLTIP_POSITION} from '@Components/tooltip';
import {FontFamilyOptionType} from '@Components/poster-editor/components/poster-editing-side-panel/components/item-with-dual-font-options/item-with-dual-font-options.types';
import styles from './item-with-dual-font-options.module.scss';
import {useSingleActivePosterItemId} from '@Hooks/poster-editor/useSingleActivePosterItemId';
import {useGetKeyFromTextStylesActiveItem} from '@Hooks/poster-editor/useGetKeyFromTextStylesActiveItem';
import {useGetKeyFromTableItem} from '@Hooks/poster-editor/useGetKeyFromTableItem';

interface ItemWithDualFontOptionsProps {
  onClickFontButton(clickedFontOption: FontFamilyOptionType): void;
}

export function ItemWithDualFontOptions({...props}: ItemWithDualFontOptionsProps): ReactElement<any> {
  const activeItemId = useSingleActivePosterItemId() ?? '';
  const activeItem = activeItemId ? window.posterEditor?.whiteboard?.getItemForId(activeItemId) : undefined;
  const itemStylesStroke = useGetKeyFromTextStylesActiveItem('stroke');
  const itemFontFamily = useGetKeyFromTextStylesActiveItem('fontFamily');
  const itemIsBoldTwo = useGetKeyFromTableItem('isBold2');
  const itemIsItalicTwo = useGetKeyFromTableItem('isItalic2');
  const itemLinethroughTwo = useGetKeyFromTableItem('lineThrough2');
  const itemUnderlineTwo = useGetKeyFromTableItem('underLine2');
  const itemFontFamilyTwo = useGetKeyFromTableItem('fontFamily2');

  if (!activeItem || (activeItem && !('textStyles' in activeItem)) || itemFontFamily === undefined || itemStylesStroke === undefined) {
    throw new Error('Item does not exist or text styles property does not exist in active item');
  }

  if (
    !('fontFamily2' in activeItem) ||
    !('isBold2' in activeItem) ||
    !('isItalic2' in activeItem) ||
    !('underLine2' in activeItem) ||
    !('lineThrough2' in activeItem) ||
    itemIsBoldTwo === undefined ||
    itemFontFamilyTwo === undefined ||
    itemLinethroughTwo === undefined ||
    itemUnderlineTwo === undefined ||
    itemIsItalicTwo === undefined
  ) {
    throw new Error('Item does not have font 2 property (font family 2 - isBold2 - isItalic2 - underline2 - lineThrough2)');
  }

  const onClickHandler = (clickedOption: FontFamilyOptionType): void => {
    props.onClickFontButton(clickedOption);
  };

  const getSubOptionForFontOne = (): ReactElement<any> => {
    return <ItemStylingOptions tooltipPosition={TOOLTIP_POSITION.BOTTOM_MIDDLE} tooltipClassName={styles.tooltip} />;
  };

  const onClickBoldForFontTwo = (): void => {
    activeItem.toggleBold2();
  };
  const onClickItalicForFontTwo = (): void => {
    activeItem.toggleItalic2();
  };
  const onClickStrikeThroughForFontTwo = (): void => {
    activeItem.toggleLineThrough2();
  };
  const onClickUnderlineForFontTwo = (): void => {
    activeItem.toggleUnderline2();
  };

  const getSubOptionForFontTwo = (): ReactElement<any> => {
    return (
      <BaseStylingOptions
        isBold={itemIsBoldTwo}
        isBoldDisabled={itemStylesStroke}
        isItalic={itemIsItalicTwo}
        lineThrough={itemLinethroughTwo}
        underLine={itemUnderlineTwo}
        onClickBold={onClickBoldForFontTwo}
        onClickItalic={onClickItalicForFontTwo}
        onClickLineThrough={onClickStrikeThroughForFontTwo}
        onClickUnderline={onClickUnderlineForFontTwo}
        tooltipPosition={TOOLTIP_POSITION.BOTTOM_MIDDLE}
        tooltipClassName={`${styles.tooltip} `}
      />
    );
  };

  return (
    <>
      <ContextualMenuFontButton
        id="menu-item-font-one"
        label={window.i18next.t('pmjws_font_one')}
        selectedFontFamily={itemFontFamily}
        onClickFontButton={(): void => {
          onClickHandler(FontFamilyOptionType.ONE);
        }}
        subOptionHasOverflow
      >
        {getSubOptionForFontOne()}
      </ContextualMenuFontButton>
      <ContextualMenuFontButton
        id="menu-item-font-one"
        label={window.i18next.t('pmjws_font_two')}
        selectedFontFamily={itemFontFamilyTwo}
        onClickFontButton={(): void => {
          onClickHandler(FontFamilyOptionType.TWO);
        }}
        subOptionHasOverflow
      >
        {getSubOptionForFontTwo()}
      </ContextualMenuFontButton>
    </>
  );
}
